import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore,
  ExpandLess,
  Settings as SettingsIcon,
} from '@mui/icons-material';

/**
 * Shared legend row + 5-column grid used by Price Monitoring and Total Ownership KPI charts.
 * Optional "Show missing data as" controls when `missingDataMode` + `onMissingDataModeChange` are passed.
 */
const MonitoringChartLegend = React.memo(
  ({
    datasets,
    visibleDatasets,
    onToggleDataset,
    onShowAll,
    onHideAll,
    searchTerm,
    onSearchChange,
    missingDataMode,
    onMissingDataModeChange,
    legendExpanded = true,
    onLegendExpandToggle,
    onAxisSettingsOpen,
    axisSettingsActive,
  }) => {
    const showMissingDataControls =
      missingDataMode != null && typeof onMissingDataModeChange === 'function';

    const filteredDatasets = useMemo(() => {
      const filtered = !searchTerm
        ? datasets
        : datasets.filter(
            (ds) => ds.label && ds.label.toLowerCase().includes(searchTerm.toLowerCase())
          );
      return [...filtered].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    }, [datasets, searchTerm]);

    const allVisible =
      filteredDatasets.length > 0 &&
      filteredDatasets.every((ds) => visibleDatasets.has(ds.label));
    const allHidden =
      filteredDatasets.length > 0 &&
      filteredDatasets.every((ds) => !visibleDatasets.has(ds.label));

    return (
      <Box sx={{ pt: 0.5, borderTop: '1px solid #e0e0e0', mt: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: legendExpanded ? 0.5 : 0,
            flexWrap: 'wrap',
          }}
        >
          {legendExpanded && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={onShowAll}
                disabled={allVisible}
                sx={{
                  fontSize: '0.6rem',
                  py: 0.1,
                  px: 0.75,
                  minWidth: 0,
                  height: 20,
                  lineHeight: 1,
                }}
              >
                All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={onHideAll}
                disabled={allHidden}
                sx={{
                  fontSize: '0.6rem',
                  py: 0.1,
                  px: 0.75,
                  minWidth: 0,
                  height: 20,
                  lineHeight: 1,
                }}
              >
                None
              </Button>
              <TextField
                size="small"
                placeholder="Search…"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '0.8rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': { height: 20, fontSize: '0.65rem' },
                  '& .MuiInputBase-input': { py: 0, px: 0.5 },
                  width: 110,
                }}
              />
              {showMissingDataControls && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      mr: 0.5,
                    }}
                  >
                    Show missing data as:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={missingDataMode}
                      onChange={(e) => onMissingDataModeChange(e.target.value)}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        '& .MuiSelect-select': { py: 0, px: 0.75 },
                      }}
                    >
                      <MenuItem value="no line" sx={{ fontSize: '0.65rem' }}>
                        No line
                      </MenuItem>
                      <MenuItem value="dashed line" sx={{ fontSize: '0.65rem' }}>
                        Dashed line
                      </MenuItem>
                      <MenuItem value="full line" sx={{ fontSize: '0.65rem' }}>
                        Full line
                      </MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {onLegendExpandToggle && (
            <Tooltip title={legendExpanded ? 'Collapse legend' : 'Expand legend'} arrow>
              <IconButton
                size="small"
                onClick={onLegendExpandToggle}
                sx={{ color: 'text.secondary', p: 0.25 }}
              >
                {legendExpanded ? (
                  <ExpandLess sx={{ fontSize: '0.95rem' }} />
                ) : (
                  <ExpandMore sx={{ fontSize: '0.95rem' }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {onAxisSettingsOpen && (
            <Tooltip title="Axis settings" arrow>
              <IconButton
                size="small"
                onClick={(e) => onAxisSettingsOpen(e.currentTarget)}
                sx={{
                  color: axisSettingsActive ? 'primary.main' : 'text.disabled',
                  p: 0.25,
                }}
              >
                <SettingsIcon sx={{ fontSize: '0.95rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {legendExpanded && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gridTemplateRows: `repeat(${Math.ceil(filteredDatasets.length / 5)}, auto)`,
              gridAutoFlow: 'column',
              gap: '2px 6px',
              maxHeight: 120,
              overflowY: 'auto',
            }}
          >
            {filteredDatasets.length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                No matches
              </Typography>
            ) : (
              filteredDatasets.map((dataset, idx) => {
                const isVisible = visibleDatasets.has(dataset.label);
                return (
                  <Box
                    key={dataset.label || idx}
                    onClick={() => onToggleDataset(dataset.label)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      cursor: 'pointer',
                      opacity: isVisible ? 1 : 0.4,
                      px: 0.5,
                      py: '1px',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <svg width="16" height="4" style={{ flexShrink: 0 }}>
                      <line
                        x1="0"
                        y1="2"
                        x2="16"
                        y2="2"
                        stroke={dataset.borderColor || dataset.backgroundColor}
                        strokeWidth="2.5"
                        strokeDasharray={
                          dataset.borderDash && dataset.borderDash.length
                            ? dataset.borderDash.join(' ')
                            : undefined
                        }
                        strokeLinecap="round"
                      />
                    </svg>
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textDecoration: isVisible ? 'none' : 'line-through',
                        color: isVisible ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {dataset.label}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Box>
    );
  }
);

export default MonitoringChartLegend;
